import React, { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
  LineChart, Line,
} from 'recharts';
import Card from '../../../components/ui/Card';
import { translateTerm as translateTermUtil } from '../utils';

const fmtBRL = (v) => {
  if (v == null || isNaN(v)) return '-';
  if (Math.abs(v) >= 1e9) return `R$ ${(v / 1e9).toFixed(1)}B`;
  if (Math.abs(v) >= 1e6) return `R$ ${(v / 1e6).toFixed(1)}M`;
  return `R$ ${Number(v).toLocaleString('pt-BR')}`;
};

export default function AssetsTab({ lang, year, petrobrasData, refDate, t, setSelectedAsset, timelineData }) {
  const translateTerm = (term) => translateTermUtil(term, lang);

  const intangivelRows = petrobrasData['ativo_intangivel'] || [];
  const intangivelFallback = intangivelRows.length === 0;

  const imobilizadoRows = petrobrasData['ativo_imobilizado'] || [];
  const imobilizadoFallback = imobilizadoRows.length === 0;

  // Total assets per year line chart
  const totalAssetsChartData = useMemo(() => {
    const history = (timelineData && timelineData.dfpHistory) ? timelineData.dfpHistory : [];
    const byPeriod = {};
    history.filter(r => r.source === 'dfp' && r.descricao === 'Ativo Total').forEach(r => {
      if (!byPeriod[r.period] || r.value > byPeriod[r.period]) byPeriod[r.period] = r.value;
    });
    return Object.entries(byPeriod)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, value]) => ({
        period: period.substring(0, 4),
        [lang === 'pt' ? 'Ativo Total' : 'Total Assets']: +(value / 1e9).toFixed(2),
      }));
  }, [timelineData, lang]);

  // CVM BPA intangibles from dfpHistory (account 1.02.04)
  const cvmIntangRows = useMemo(() => {
    const history = (timelineData && timelineData.dfpHistory) ? timelineData.dfpHistory : [];
    // Keep only DFP annual rows (source='dfp') for Intangível to avoid duplicate ITR entries
    const dfpOnly = history.filter(r => r.descricao === 'Ativos Intangíveis e Concessões' && r.source === 'dfp');
    // Deduplicate by period, keeping highest value
    const byPeriod = {};
    dfpOnly.forEach(r => {
      if (!byPeriod[r.period] || r.value > byPeriod[r.period].value) byPeriod[r.period] = r;
    });
    return Object.values(byPeriod).sort((a, b) => b.period.localeCompare(a.period));
  }, [timelineData]);

  // Asset composition for the latest DFP period (pie chart)
  const assetCompositionData = useMemo(() => {
    const history = (timelineData && timelineData.dfpHistory) ? timelineData.dfpHistory : [];
    const dfpOnly = history.filter(r => r.source === 'dfp');
    const latestPeriod = dfpOnly.reduce((max, r) => r.period > max ? r.period : max, '');
    if (!latestPeriod) return [];
    const vals = {};
    dfpOnly.filter(r => r.period === latestPeriod).forEach(r => { vals[r.descricao] = r.value; });
    const total = vals['Ativo Total'] || 0;
    const CATS = [
      { key: 'Ativo Circulante',              label: lang === 'pt' ? 'Circulante'   : 'Current',      color: '#3b82f6' },
      { key: 'Realizável a LP',               label: lang === 'pt' ? 'Realizável LP': 'LT Receiv.',    color: '#8b5cf6' },
      { key: 'Investimentos',                 label: lang === 'pt' ? 'Investimentos': 'Investments',   color: '#06b6d4' },
      { key: 'Imobilizado',                   label: lang === 'pt' ? 'Imobilizado'  : 'PP&E',          color: '#f97316' },
      { key: 'Ativos Intangíveis e Concessões', label: lang === 'pt' ? 'Intangível'   : 'Intangible',   color: '#eab308' },
    ];
    const items = CATS.filter(c => vals[c.key] > 0).map(c => ({ name: c.label, value: vals[c.key], color: c.color }));
    const known = items.reduce((s, i) => s + i.value, 0);
    const outros = Math.max(0, total - known);
    if (outros > 0) items.push({ name: lang === 'pt' ? 'Outros' : 'Others', value: outros, color: '#6b7280' });
    return { items, latestYear: latestPeriod.substring(0, 4) };
  }, [timelineData, lang]);

  // Per-year chart data: stacked categories from dfpHistory
  const intangChartData = useMemo(() => {
    const history = (timelineData && timelineData.dfpHistory) ? timelineData.dfpHistory : [];
    const CATS = {
      'Ativos Intangíveis e Concessões': 'total',
      'Intangíveis': 'intangiveis',
      'Goodwill': 'goodwill',
    };
    const byPeriod = {};
    history.filter(r => r.source === 'dfp' && CATS[r.descricao]).forEach(r => {
      if (!byPeriod[r.period]) byPeriod[r.period] = {};
      byPeriod[r.period][CATS[r.descricao]] = r.value;
    });
    return Object.entries(byPeriod)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, vals]) => {
        const total      = vals.total      || 0;
        const intangiveis = vals.intangiveis || 0;
        const goodwill   = vals.goodwill    || 0;
        const outros     = Math.max(0, total - intangiveis - goodwill);
        return {
          period: period.substring(0, 4),
          [lang === 'pt' ? 'Intangíveis' : 'Intangibles']:     +(intangiveis / 1e9).toFixed(2),
          'Goodwill':                                           +(goodwill    / 1e9).toFixed(2),
          [lang === 'pt' ? 'Não classificados' : 'Unclassified']: +(outros      / 1e9).toFixed(2),
        };
      });
  }, [timelineData, lang]);

  const INTANG_COLORS = ['#f59e0b', '#6366f1', '#10b981'];
  const intangKeys = intangChartData.length > 0
    ? Object.keys(intangChartData[0]).filter(k => k !== 'period')
    : [];

  // CVM BPA imobilizado from dfpHistory (account 1.02.03) — for fallback table
  const cvmImobRows = useMemo(() => {
    const history = (timelineData && timelineData.dfpHistory) ? timelineData.dfpHistory : [];
    const dfpOnly = history.filter(r => r.descricao === 'Imobilizado' && r.source === 'dfp');
    const byPeriod = {};
    dfpOnly.forEach(r => {
      if (!byPeriod[r.period] || r.value > byPeriod[r.period].value) byPeriod[r.period] = r;
    });
    return Object.values(byPeriod).sort((a, b) => b.period.localeCompare(a.period));
  }, [timelineData]);

  // Per-year chart data for imobilizado with sub-account breakdown
  const imobChartData = useMemo(() => {
    const history = (timelineData && timelineData.dfpHistory) ? timelineData.dfpHistory : [];
    const CATS = {
      'Imobilizado':          'total',
      'Imob. em Operação':    'operacao',
      'Arrendamento':         'lease',
      'Imob. em Andamento':   'andamento',
    };
    const byPeriod = {};
    history.filter(r => r.source === 'dfp' && CATS[r.descricao]).forEach(r => {
      if (!byPeriod[r.period]) byPeriod[r.period] = {};
      byPeriod[r.period][CATS[r.descricao]] = r.value;
    });
    return Object.entries(byPeriod)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, vals]) => {
        const total     = vals.total     || 0;
        const operacao  = vals.operacao  || 0;
        const lease     = vals.lease     || 0;
        const andamento = vals.andamento || 0;
        const outros    = Math.max(0, total - operacao - lease - andamento);
        return {
          period: period.substring(0, 4),
          [lang === 'pt' ? 'Em Operação'       : 'In Operation']:        +(operacao  / 1e9).toFixed(2),
          [lang === 'pt' ? 'Em Andamento'      : 'Under Construction']:  +(andamento / 1e9).toFixed(2),
          [lang === 'pt' ? 'Arrendamento'      : 'Lease']:               +(lease     / 1e9).toFixed(2),
          [lang === 'pt' ? 'Não classificados' : 'Unclassified']:        +(outros    / 1e9).toFixed(2),
        };
      });
  }, [timelineData, lang]);

  const IMOB_COLORS = ['#f97316', '#fb923c', '#a78bfa', '#6b7280'];
  const imobKeys = imobChartData.length > 0
    ? Object.keys(imobChartData[0]).filter(k => k !== 'period')
    : [];

  const fallbackNotice = () => (
    <p className="text-[10px] text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2 mb-4">
      {lang === 'pt'
        ? 'Este campo não é reportado no FRE pela Petrobras. Exibindo dados do CVM/BPA (conta 1.02.04 – Intangível).'
        : 'This field is not reported in FRE by Petrobras. Showing CVM/BPA data (account 1.02.04 – Intangível).'}
    </p>
  );

  return (
    <div className="space-y-6">

      {/* Total Assets line chart + Asset Composition — side by side on large screens */}
      {(totalAssetsChartData.length > 0 || assetCompositionData.items?.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {totalAssetsChartData.length > 0 && (
            <Card
              title={lang === 'pt' ? 'Evolução do Ativo Total' : 'Total Assets Evolution'}
              subtitle={lang === 'pt'
                ? `Ativo total consolidado por ano (Fonte: CVM/BPA) · ${totalAssetsChartData[0]?.period}–${totalAssetsChartData[totalAssetsChartData.length - 1]?.period}`
                : `Consolidated total assets by year (Source: CVM/BPA) · ${totalAssetsChartData[0]?.period}–${totalAssetsChartData[totalAssetsChartData.length - 1]?.period}`}
              refDate={refDate}
            >
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={totalAssetsChartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="period" tick={{ fontSize: 10, fill: 'var(--muted)' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} tickFormatter={v => `${v}B`} width={42} />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}
                    formatter={(v, name) => [`R$ ${v}B`, name]}
                  />
                  <Line
                    type="monotone"
                    dataKey={lang === 'pt' ? 'Ativo Total' : 'Total Assets'}
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: '#3b82f6', strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          {assetCompositionData.items?.length > 0 && (
            <Card
              title={lang === 'pt' ? 'Composição dos Ativos' : 'Asset Composition'}
              subtitle={lang === 'pt'
                ? `Distribuição dos ativos totais – ${assetCompositionData.latestYear} (Fonte: CVM/BPA)`
                : `Total asset distribution – ${assetCompositionData.latestYear} (Source: CVM/BPA)`}
              refDate={refDate}
            >
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
                <div className="w-full sm:w-48 shrink-0">
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie
                        data={assetCompositionData.items}
                        cx="50%" cy="50%"
                        innerRadius={52} outerRadius={78}
                        dataKey="value"
                        nameKey="name"
                      >
                        {assetCompositionData.items.map((entry, i) => (
                          <Cell key={i} fill={entry.color} stroke="var(--card)" strokeWidth={2} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}
                        formatter={(v, name) => [fmtBRL(v), name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  {assetCompositionData.items.map(item => {
                    const pct = assetCompositionData.items.reduce((s, i) => s + i.value, 0);
                    const share = pct > 0 ? ((item.value / pct) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={item.name} className="flex items-center gap-2">
                        <span style={{ background: item.color, width: 10, height: 10, borderRadius: 2, flexShrink: 0, display: 'inline-block' }} />
                        <span className="text-xs text-[var(--muted)] flex-1">{item.name}</span>
                        <span className="text-xs font-mono text-[var(--text)]">{fmtBRL(item.value)}</span>
                        <span className="text-[10px] text-[var(--muted)] w-12 text-right">{share}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}

        </div>
      )}

      {/* ── Ativo Imobilizado Card (PP&E) — shown BEFORE intangibles ── */}
      <Card
        title={t.card_imobilizado}
        subtitle={imobilizadoFallback
          ? (lang === 'pt' ? 'Fonte: CVM/BPA – conta 1.02.03' : 'Source: CVM/BPA – account 1.02.03')
          : (lang === 'pt' ? `Exibindo 10 de ${imobilizadoRows.length} registros` : `Showing 10 of ${imobilizadoRows.length} records`)}
        refDate={refDate}
      >
        {imobilizadoFallback && (
          <p className="text-[10px] text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2 mb-4">
            {lang === 'pt'
              ? 'Este campo não é reportado no FRE pela Petrobras. Exibindo dados do CVM/BPA (conta 1.02.03 – Imobilizado).'
              : 'This field is not reported in FRE by Petrobras. Showing CVM/BPA data (account 1.02.03 – Imobilizado).'}
          </p>
        )}
        {/* Trend bar chart */}
        {imobChartData.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] text-[var(--muted)] font-medium uppercase tracking-wider mb-1">
              {lang === 'pt' ? 'Evolução Ativo Imobilizado (R$ B)' : 'PP&E Trend (R$ B)'}
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={imobChartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: 'var(--muted)' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} tickFormatter={v => `${v}B`} width={38} />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}
                  formatter={(v, name) => [`R$ ${v}B`, name]}
                />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 10 }} />
                {imobKeys.map((key, i) => (
                  <Bar key={key} dataKey={key} stackId="a" fill={IMOB_COLORS[i % IMOB_COLORS.length]} radius={i === imobKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {/* Table: FRE descriptive data or CVM annual fallback */}
        {!imobilizadoFallback ? (
          <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                  <th className="p-2 sm:p-3.5 font-semibold">{t.th_asset_desc}</th>
                  <th className="p-2 sm:p-3.5 font-semibold hidden sm:table-cell">{t.th_location}</th>
                  <th className="p-2 sm:p-3.5 font-semibold">{t.th_prop_type}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {imobilizadoRows.slice(0, 10).map((item, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-[var(--accent-soft)]/20 cursor-pointer transition-colors"
                    onClick={() => setSelectedAsset({ ...item, assetType: 'imobilizado' })}
                  >
                    <td className="p-2 sm:p-3.5 font-bold text-[var(--text)]">{translateTerm(item.Descricao_Bem_Ativo)}</td>
                    <td className="p-2 sm:p-3.5 text-[var(--muted)] hidden sm:table-cell">{`${item.Municipio || ''} - ${item.UF || ''}, ${translateTerm(item.Pais)}`}</td>
                    <td className="p-2 sm:p-3.5 text-[var(--text)]">{translateTerm(item.Tipo_Propriedade)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : cvmImobRows.length > 0 ? (
          <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                  <th className="p-2 sm:p-3.5 font-semibold">{lang === 'pt' ? 'Período' : 'Period'}</th>
                  <th className="p-2 sm:p-3.5 font-semibold">{lang === 'pt' ? 'Ativo' : 'Asset'}</th>
                  <th className="p-2 sm:p-3.5 font-semibold text-right">{lang === 'pt' ? 'Valor' : 'Value'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {cvmImobRows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-[var(--accent-soft)]/20 transition-colors">
                    <td className="p-2 sm:p-3.5 text-[var(--muted)]">{row.period}</td>
                    <td className="p-2 sm:p-3.5 font-bold text-[var(--text)]">{row.descricao}</td>
                    <td className="p-2 sm:p-3.5 text-right font-mono text-[var(--text)]">{fmtBRL(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {/* ── Ativos Intangíveis Card — after imobilizado ── */}
      <Card
        title={t.card_intangible}
        subtitle={intangivelFallback
          ? (lang === 'pt' ? 'Fonte: CVM/BPA – conta 1.02.04' : 'Source: CVM/BPA – account 1.02.04')
          : (lang === 'pt' ? `Exibindo 10 de ${intangivelRows.length} registros` : `Showing 10 of ${intangivelRows.length} records`)}
        refDate={refDate}
      >
        {intangivelFallback && fallbackNotice()}
        {intangivelFallback && cvmIntangRows.length > 0 ? (
          <>
            {/* Trend bar chart */}
            {(intangChartData.length > 0) && (
              <div className="mb-4">
                <p className="text-[10px] text-[var(--muted)] font-medium uppercase tracking-wider mb-1">
                  {lang === 'pt' ? 'Evolução Intangíveis (R$ B)' : 'Intangibles Trend (R$ B)'}
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={intangChartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="period" tick={{ fontSize: 10, fill: 'var(--muted)' }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} tickFormatter={v => `${v}B`} width={38} />
                    <Tooltip
                      contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}
                      formatter={(v, name) => [`R$ ${v}B`, name]}
                    />
                    <Legend iconSize={9} wrapperStyle={{ fontSize: 10 }} />
                    {intangKeys.map((key, i) => (
                      <Bar key={key} dataKey={key} stackId="a" fill={INTANG_COLORS[i % INTANG_COLORS.length]} radius={i === intangKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                  <th className="p-2 sm:p-3.5 font-semibold">{lang === 'pt' ? 'Período' : 'Period'}</th>
                  <th className="p-2 sm:p-3.5 font-semibold">{lang === 'pt' ? 'Ativo' : 'Asset'}</th>
                  <th className="p-2 sm:p-3.5 font-semibold hidden sm:table-cell">{lang === 'pt' ? 'Descrição' : 'Description'}</th>
                  <th className="p-2 sm:p-3.5 font-semibold text-right">{lang === 'pt' ? 'Valor' : 'Value'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {cvmIntangRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-[var(--accent-soft)]/20 cursor-pointer transition-colors"
                    onClick={() => setSelectedAsset({ ...row, assetType: 'cvm_intang', cdConta: '1.02.04' })}
                  >
                    <td className="p-2 sm:p-3.5 text-[var(--muted)]">{row.period}</td>
                    <td className="p-2 sm:p-3.5 font-bold text-[var(--text)]">{row.descricao}</td>
                    <td className="p-2 sm:p-3.5 text-[var(--muted)] max-w-xs hidden sm:table-cell">
                      {lang === 'pt'
                        ? 'Direitos de concessão, softwares, patentes e goodwill registrados pela Petrobras.'
                        : 'Concession rights, software, patents and goodwill recorded by Petrobras.'}
                    </td>
                    <td className="p-2 sm:p-3.5 text-right font-mono text-[var(--text)]">{fmtBRL(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        ) : !intangivelFallback ? (
          <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                  <th className="p-2 sm:p-3.5 font-semibold">{t.th_asset_type}</th>
                  <th className="p-2 sm:p-3.5 font-semibold">{t.th_description}</th>
                  <th className="p-2 sm:p-3.5 font-semibold hidden sm:table-cell">{t.th_duration}</th>
                  <th className="p-2 sm:p-3.5 font-semibold hidden md:table-cell">{t.th_loss_event}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {intangivelRows.slice(0, 10).map((item, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-[var(--accent-soft)]/20 cursor-pointer transition-colors"
                    onClick={() => setSelectedAsset({ ...item, assetType: 'intangible' })}
                  >
                    <td className="p-2 sm:p-3.5">
                      <span className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 dark:text-yellow-400 text-[10px] px-2.5 py-0.5 rounded font-bold uppercase tracking-wider">
                        {translateTerm(item.Tipo_Ativo)}
                      </span>
                    </td>
                    <td className="p-2 sm:p-3.5 font-bold text-[var(--text)] max-w-sm truncate" title={translateTerm(item.Descricao_Ativo)}>{translateTerm(item.Descricao_Ativo)}</td>
                    <td className="p-2 sm:p-3.5 text-[var(--text)] hidden sm:table-cell">{translateTerm(item.Duracao)}</td>
                    <td className="p-2 sm:p-3.5 text-[var(--muted)] max-w-xs truncate hidden md:table-cell" title={translateTerm(item.Evento_Perda_Direito)}>{translateTerm(item.Evento_Perda_Direito)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

    </div>
  );
}
